import { Breadcrumb, Layout, Spin, Form, Input, Button, Row, Col, Card, message, Select, InputNumber, Table, DatePicker, Divider, Typography } from 'antd';
import { Content } from 'antd/es/layout/layout';
import Headerdiv from '../layout/header';
import Footerdiv from '../layout/footer';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { APPBACKGROUND_COLOR } from '../appsettings';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Text, Title } = Typography;

const CreateInvoice = () => {
    const [form] = Form.useForm();
    const USERID = localStorage.getItem("USERID");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const [members, setMembers] = useState([]);
    const [salesItems, setSalesItems] = useState([]);

    // Invoice items state
    const [invoiceItems, setInvoiceItems] = useState([]);
    const [discount, setDiscount] = useState(0);
    const [taxRate, setTaxRate] = useState(0);

    // Fetch members for dropdown
    const fetchMembers = async (search = '') => {
        try {
            const response = await axiosClient.get(`/member/get_all_members_dropdown.php?search=${search}`);
            if (response.data.status === 'success') {
                setMembers(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching members:', error);
        }
    };

    // Fetch sales items for dropdown
    const fetchSalesItems = async (search = '') => {
        try {
            const response = await axiosClient.get(`/sales/get_all_sales_items_dropdown.php?search=${search}`);
            if (response.data.status === 'success') {
                setSalesItems(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching sales items:', error);
        }
    };

    useEffect(() => {
        fetchMembers();
        fetchSalesItems();
    }, []);

    // Add new item row
    const addItemRow = () => {
        setInvoiceItems([
            ...invoiceItems,
            {
                key: Date.now(),
                sp_id: null,
                sp_title: '',
                sp_type: '',
                sp_duration: '',
                unit_price: 0,
                sales_price: 0,
                order_qty: 1,
                sub_total: 0,
                start_date: dayjs().format('YYYY-MM-DD'),
                end_date: dayjs().format('YYYY-MM-DD')
            }
        ]);
    };

    // Remove item row
    const removeItemRow = (key) => {
        setInvoiceItems(invoiceItems.filter(item => item.key !== key));
    };

    // Update item in row
    const updateItemRow = (key, field, value) => {
        setInvoiceItems(invoiceItems.map(item => {
            if (item.key === key) {
                const updated = { ...item, [field]: value };

                // If sales item selected, populate details
                if (field === 'sp_id') {
                    const selectedItem = salesItems.find(si => si.sp_id === value);
                    if (selectedItem) {
                        updated.sp_title = selectedItem.sp_title;
                        updated.sp_type = selectedItem.sp_type;
                        updated.sp_duration = selectedItem.sp_duration;
                        updated.unit_price = parseFloat(selectedItem.sp_uprice);
                        updated.sales_price = parseFloat(selectedItem.sp_uprice);
                        updated.sub_total = updated.sales_price * updated.order_qty;
                        updated.end_date = calculateEndDate(updated.start_date, selectedItem.sp_duration, updated.order_qty);
                    }
                }

                // Recalculate sub_total
                if (field === 'sales_price' || field === 'order_qty') {
                    updated.sub_total = updated.sales_price * updated.order_qty;
                    // Recalculate end date for subscription items
                    if (updated.sp_duration && field === 'order_qty') {
                        updated.end_date = calculateEndDate(updated.start_date, updated.sp_duration, updated.order_qty);
                    }
                }

                // Recalculate end date when start date changes
                if (field === 'start_date' && updated.sp_duration) {
                    updated.end_date = calculateEndDate(value, updated.sp_duration, updated.order_qty);
                }

                return updated;
            }
            return item;
        }));
    };

    // Calculate end date based on duration
    const calculateEndDate = (startDate, duration, qty) => {
        const start = dayjs(startDate);
        switch (duration) {
            case 'DAILY':
                return start.add(qty, 'day').format('YYYY-MM-DD');
            case 'MONTHLY':
                return start.add(qty, 'month').format('YYYY-MM-DD');
            case 'ONETIME':
            default:
                return start.format('YYYY-MM-DD');
        }
    };

    // Calculate totals
    const subTotal = invoiceItems.reduce((sum, item) => sum + item.sub_total, 0);
    const taxableAmount = subTotal - discount;
    const taxAmount = Math.round(taxableAmount * (taxRate / 100));
    const invoiceTotal = taxableAmount + taxAmount;

    // Submit invoice
    const onFinish = async (values) => {
        if (invoiceItems.length === 0) {
            message.error('Please add at least one item to the invoice.');
            return;
        }

        // Validate all items have sales item selected
        const invalidItems = invoiceItems.filter(item => !item.sp_id);
        if (invalidItems.length > 0) {
            message.error('Please select a sales item for all rows.');
            return;
        }

        setLoading(true);

        const payload = {
            member_id: values.member_id,
            items: invoiceItems.map(item => ({
                sp_id: item.sp_id,
                unit_price: item.unit_price,
                sales_price: item.sales_price,
                order_qty: item.order_qty,
                start_date: item.start_date
            })),
            total_discount: discount,
            tax_rate: taxRate,
            invoice_note: values.invoice_note || '',
            invoice_currency: 'LKR',
            by: USERID
        };

        try {
            const response = await axiosClient.post('/invoice/add_invoice.php', payload);
            const data = response.data;

            if (data.status === "success") {
                message.success(`Invoice ${data.inv_ref} created successfully!`);
                navigate(`/invoices/view/${data.inv_id}`);
            } else {
                message.error(data.message || "Failed to create invoice.");
            }
        } catch (error) {
            console.error("API Error:", error);
            message.error(error.response?.data?.message || "Failed to connect to server.");
        } finally {
            setLoading(false);
        }
    };

    // Item table columns
    const itemColumns = [
        {
            title: 'Sales Item',
            dataIndex: 'sp_id',
            width: 250,
            render: (_, record) => (
                <Select
                    showSearch
                    placeholder="Select Item"
                    value={record.sp_id}
                    onChange={(value) => updateItemRow(record.key, 'sp_id', value)}
                    onSearch={(value) => fetchSalesItems(value)}
                    filterOption={false}
                    style={{ width: '100%' }}
                >
                    {salesItems.map(item => (
                        <Select.Option key={item.sp_id} value={item.sp_id}>
                            [{item.sp_code}] {item.sp_title} - Rs.{Number(item.sp_uprice).toLocaleString()}
                        </Select.Option>
                    ))}
                </Select>
            )
        },
        {
            title: 'Type',
            dataIndex: 'sp_type',
            width: 80,
            render: (type) => type || '-'
        },
        {
            title: 'Duration',
            dataIndex: 'sp_duration',
            width: 90,
            render: (duration) => duration || '-'
        },
        {
            title: 'Unit Price',
            dataIndex: 'unit_price',
            width: 100,
            align: 'right',
            render: (price) => `Rs. ${Number(price).toLocaleString()}`
        },
        {
            title: 'Sales Price',
            dataIndex: 'sales_price',
            width: 120,
            render: (_, record) => (
                <InputNumber
                    value={record.sales_price}
                    onChange={(value) => updateItemRow(record.key, 'sales_price', value || 0)}
                    min={0}
                    style={{ width: '100%' }}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value.replace(/,/g, '')}
                />
            )
        },
        {
            title: 'Qty',
            dataIndex: 'order_qty',
            width: 80,
            render: (_, record) => (
                <InputNumber
                    value={record.order_qty}
                    onChange={(value) => updateItemRow(record.key, 'order_qty', value || 1)}
                    min={1}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: 'Start Date',
            dataIndex: 'start_date',
            width: 130,
            render: (_, record) => (
                <DatePicker
                    value={dayjs(record.start_date)}
                    format="DD MMM YYYY"
                    onChange={(date) => updateItemRow(record.key, 'start_date', date ? date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'))}
                    style={{ width: '100%' }}
                    allowClear={false}
                />
            )
        },
        {
            title: 'End Date',
            dataIndex: 'end_date',
            width: 120,
            render: (date) => dayjs(date).format('DD MMM YYYY')
        },
        {
            title: 'Sub Total',
            dataIndex: 'sub_total',
            width: 110,
            align: 'right',
            render: (total) => <strong>Rs. {Number(total).toLocaleString()}</strong>
        },
        {
            title: '',
            width: 50,
            render: (_, record) => (
                <Button
                    icon={<DeleteOutlined />}
                    type="text"
                    danger
                    onClick={() => removeItemRow(record.key)}
                />
            )
        }
    ];

    return (
        <Spin spinning={loading} tip="Creating Invoice...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: '100vh' }}>
                <Headerdiv />
                <Content style={{ padding: "10px" }}>
                    <Breadcrumb style={{ marginBottom: 10 }}>
                        <Breadcrumb.Item><Link to={"/invoices"}>Invoices</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>Create Invoice</Breadcrumb.Item>
                    </Breadcrumb>

                    <Form layout="vertical" form={form} onFinish={onFinish}>
                        {/* Member Selection */}
                        <Card title="Member Information" style={{ marginBottom: 15 }}>
                            <Row gutter={[16, 16]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        name="member_id"
                                        label="Select Member"
                                        rules={[{ required: true, message: "Please select a member" }]}
                                    >
                                        <Select
                                            showSearch
                                            placeholder="Search and select member"
                                            onSearch={(value) => fetchMembers(value)}
                                            filterOption={false}
                                            style={{ width: '100%' }}
                                        >
                                            {members.map(member => (
                                                <Select.Option key={member.id} value={member.id}>
                                                    [{member.member_id}] {member.name} - {member.phone}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* Invoice Items */}
                        <Card 
                            title="Invoice Items" 
                            style={{ marginBottom: 15 }}
                            extra={
                                <Button type="primary" icon={<PlusOutlined />} onClick={addItemRow}>
                                    Add Item
                                </Button>
                            }
                        >
                            <Table
                                columns={itemColumns}
                                dataSource={invoiceItems}
                                rowKey="key"
                                pagination={false}
                                bordered
                                size="small"
                                scroll={{ x: 1200 }}
                                locale={{ emptyText: 'Click "Add Item" to add invoice items' }}
                            />
                        </Card>

                        {/* Summary */}
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <Card title="Notes">
                                    <Form.Item name="invoice_note" label="Invoice Note">
                                        <TextArea rows={4} placeholder="Enter any notes for this invoice" maxLength={100} showCount />
                                    </Form.Item>
                                </Card>
                            </Col>

                            <Col xs={24} md={12}>
                                <Card title="Invoice Summary">
                                    <Row gutter={[16, 8]}>
                                        <Col span={12}><Text>Sub Total:</Text></Col>
                                        <Col span={12} style={{ textAlign: 'right' }}>
                                            <Text strong>Rs. {subTotal.toLocaleString()}</Text>
                                        </Col>

                                        <Col span={12}><Text>Discount:</Text></Col>
                                        <Col span={12}>
                                            <InputNumber
                                                value={discount}
                                                onChange={(value) => setDiscount(value || 0)}
                                                min={0}
                                                max={subTotal}
                                                style={{ width: '100%' }}
                                                formatter={value => `Rs. ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                parser={value => value.replace(/Rs\.\s?|(,*)/g, '')}
                                            />
                                        </Col>

                                        <Col span={12}><Text>Tax Rate (%):</Text></Col>
                                        <Col span={12}>
                                            <InputNumber
                                                value={taxRate}
                                                onChange={(value) => setTaxRate(value || 0)}
                                                min={0}
                                                max={100}
                                                style={{ width: '100%' }}
                                                formatter={value => `${value}%`}
                                                parser={value => value.replace('%', '')}
                                            />
                                        </Col>

                                        <Col span={12}><Text>Tax Amount:</Text></Col>
                                        <Col span={12} style={{ textAlign: 'right' }}>
                                            <Text>Rs. {taxAmount.toLocaleString()}</Text>
                                        </Col>

                                        <Divider style={{ margin: '8px 0' }} />

                                        <Col span={12}><Title level={5} style={{ margin: 0 }}>Invoice Total:</Title></Col>
                                        <Col span={12} style={{ textAlign: 'right' }}>
                                            <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                                                Rs. {invoiceTotal.toLocaleString()}
                                            </Title>
                                        </Col>
                                    </Row>

                                    <Divider />

                                    <Row justify="end" gutter={[10, 10]}>
                                        <Col>
                                            <Button onClick={() => navigate('/invoices')}>Cancel</Button>
                                        </Col>
                                        <Col>
                                            <Button type="primary" htmlType="submit" disabled={invoiceItems.length === 0}>
                                                Create Invoice
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default CreateInvoice;
